import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { Button, ConfirmationDialog, Textarea } from '@roger/ui-components';
import {
  createEpisodeNote,
  deleteEpisodeNote,
  listEpisodeNotes,
  updateEpisodeNote,
} from '../../services/api';
import type { EpisodeNoteResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { useStore } from '../../store';
import { formatEpisodeNoteTimestamp } from '../../utils/episodeNotes';

const MAX_NOTE_LENGTH = 10_000;

// How far updated_at has to sit past created_at before an entry reads as
// edited. An insert stamps the two from adjacent clock reads, so they are never
// exactly equal and comparing them for equality would mark every note ever
// written as edited. This only has to be wide enough to clear that gap.
const EDITED_THRESHOLD_MS = 2_000;

export interface EpisodeNotesProps {
  episodeId: string;
  /**
   * Renders the log read-only when false: no add form, and no edit or delete
   * controls on the reader's own entries. The task detail panels pass
   * `!isDeleted`, so a deleted task shows its episode's notes but changes
   * none of them.
   */
  canAdd?: boolean;
  title?: string;
  description?: string;
  className?: string;
}

function formatAuthorName(note: EpisodeNoteResponse): string {
  const author = note.created_by;
  const fullName = [author.first_name, author.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || author.username;
}

function wasEdited(note: EpisodeNoteResponse): boolean {
  const created = new Date(note.created_at).getTime();
  const updated = new Date(note.updated_at).getTime();
  if (Number.isNaN(created) || Number.isNaN(updated)) {
    return false;
  }
  return updated - created > EDITED_THRESHOLD_MS;
}

/**
 * Folds a freshly loaded list together with anything saved while it was in
 * flight.
 *
 * A save can finish before the list it is being added to arrives. That list is
 * a snapshot of the log from before the write, so applying it verbatim would
 * drop the note the clinician just added; a snapshot taken after the write
 * would instead show it twice. Reconciling by id covers both: the server's
 * entries win, and a locally saved note the snapshot predates is kept at the
 * end, where its timestamp puts it.
 *
 * Reconciling by id alone is only enough because an add is the one write that
 * can overlap a load: the add form is on screen throughout, while the entries
 * are hidden behind the spinner, so nothing can be edited or deleted until the
 * load has landed. Giving this list a background refetch would break that, and
 * a stale snapshot would then undo an edit or restore a deleted entry; it would
 * have to start comparing updated_at for ids present in both lists.
 */
function mergeNotes(
  loaded: EpisodeNoteResponse[],
  appended: EpisodeNoteResponse[]
): EpisodeNoteResponse[] {
  if (appended.length === 0) {
    return loaded;
  }
  const loadedIds = new Set(loaded.map(entry => entry.id));
  const missing = appended.filter(entry => !loadedIds.has(entry.id));
  return missing.length > 0 ? [...loaded, ...missing] : loaded;
}

/**
 * An episode's shared note log, rendered wherever the episode is in context.
 *
 * Each save is a new entry carrying its author and timestamp, and the server
 * returns them oldest first, which is the order they read in. An entry can
 * afterwards be rewritten or removed by the person who wrote it, and by nobody
 * else: the controls are hidden for everyone else, and the server refuses them
 * too.
 */
const EpisodeNotes: React.FC<EpisodeNotesProps> = ({
  episodeId,
  canAdd = true,
  title = 'Episode Notes',
  description,
  className,
}) => {
  const { error: showError, success: showSuccess } = useToast();
  const currentUserId = useStore(state => state.user?.id);
  const [notes, setNotes] = useState<EpisodeNoteResponse[]>([]);
  const [draftBody, setDraftBody] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // At most one entry is open for editing or awaiting a delete confirmation at
  // a time, so the compact list never grows two sets of controls at once.
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editDraftBody, setEditDraftBody] = useState('');
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<string | null>(
    null
  );
  const [isMutating, setIsMutating] = useState(false);
  // Adding, editing and deleting all write to the same log, so one request in
  // flight closes the controls for the other two rather than each path only
  // guarding itself.
  const isBusy = isSaving || isMutating;
  const listRef = useRef<HTMLUListElement>(null);
  // The newest entry is at the bottom, so a fresh log and a just-added note
  // both want the list scrolled there. An edit or a delete does not: those
  // change an entry the reader is looking at, and jumping the viewport away
  // from it would lose their place in a long log. Both produce a new array,
  // so the effect cannot tell them apart on its own and is told instead.
  const scrollToLatestRef = useRef(true);
  // Which request is current. A monotonic token rather than the episode id:
  // on an A to B to A switch both A requests carry the same id, so an id alone
  // cannot tell the newer response from the older one and a slow first
  // response could overwrite a newer one, hiding notes until a refresh.
  const requestTokenRef = useRef(0);

  // Notes belong to the episode they were loaded for, so a change of episode
  // clears them in the same render rather than in a passive effect. Waiting
  // for the effect leaves one painted frame showing the previous chart's
  // clinical notes under the new one.
  // The draft goes with them: text typed for one episode is not a note for the
  // next one.
  const [renderedEpisodeId, setRenderedEpisodeId] = useState(episodeId);
  const renderedEpisodeIdRef = useRef(episodeId);
  if (renderedEpisodeId !== episodeId) {
    setRenderedEpisodeId(episodeId);
    renderedEpisodeIdRef.current = episodeId;
    // Retire the in-flight request here rather than leaving that to the effect
    // that starts the next one. Effects run after the commit, so between this
    // render and that effect a response for the previous episode can still
    // land, and mergeNotes would then fold its notes into the new episode's
    // list. That the new list is the only thing mergeNotes can be handed is
    // this guard's job, not React's flush order.
    requestTokenRef.current += 1;
    setNotes([]);
    setDraftBody('');
    setEditingNoteId(null);
    setEditDraftBody('');
    setPendingDeleteNoteId(null);
    setIsLoading(true);
    scrollToLatestRef.current = true;
  }

  const loadNotes = useCallback(async () => {
    const token = ++requestTokenRef.current;
    setIsLoading(true);
    try {
      const response = await listEpisodeNotes({
        path: { episode_id: episodeId },
      });
      if (requestTokenRef.current !== token) {
        return;
      }
      if (response.error) {
        throw response.error;
      }
      scrollToLatestRef.current = true;
      setNotes(previous => mergeNotes(response.data ?? [], previous));
    } catch (error) {
      if (requestTokenRef.current !== token) {
        return;
      }
      console.error('Failed to load episode notes:', error);
      showError('Unable to load episode notes', 'Please try again.');
      // Whatever is in state is left alone. A failed load has nothing of its
      // own to show, and the switch of episode above is what clears the
      // previous chart's notes, so the only thing here to clear would be a
      // note saved while this load was in flight — one the server accepted and
      // the success toast already confirmed. Dropping it would invite the
      // clinician to write it a second time.
    } finally {
      if (requestTokenRef.current === token) {
        setIsLoading(false);
      }
    }
  }, [episodeId, showError]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  useLayoutEffect(() => {
    if (isLoading || !scrollToLatestRef.current) {
      return;
    }
    scrollToLatestRef.current = false;
    const list = listRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }, [isLoading, notes]);

  const handleSave = async () => {
    const body = draftBody.trim();
    if (!body || isBusy) {
      return;
    }
    // The note is written to the episode that was on screen when the save
    // started, which is not necessarily the one on screen when it finishes.
    const savedEpisodeId = episodeId;
    setIsSaving(true);
    try {
      const response = await createEpisodeNote({
        path: { episode_id: savedEpisodeId },
        body: { body },
      });
      if (response.error) {
        throw response.error;
      }
      if (renderedEpisodeIdRef.current !== savedEpisodeId) {
        // The user moved to another episode mid-flight. The note is saved and
        // will be there on the way back, but appending it here would file a
        // care note under the wrong episode until the next refresh.
        return;
      }
      const created = response.data;
      if (created) {
        scrollToLatestRef.current = true;
        setNotes(previous => [...previous, created]);
      }
      setDraftBody('');
      showSuccess('Note added', 'Your note was added to this episode.');
    } catch (error) {
      // Reported even after the user moves on, unlike a failed load: a failed
      // save means their text was lost, and silence would hide that.
      console.error('Failed to save episode note:', error);
      showError('Failed to add note', 'Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (note: EpisodeNoteResponse) => {
    setPendingDeleteNoteId(null);
    setEditingNoteId(note.id);
    setEditDraftBody(note.body);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditDraftBody('');
  };

  const handleSaveEdit = async (noteId: string) => {
    const body = editDraftBody.trim();
    if (!body || isBusy) {
      return;
    }
    // Pinned for the same reason handleSave pins it: the entry belongs to the
    // episode that was on screen when the request started.
    const savedEpisodeId = episodeId;
    setIsMutating(true);
    try {
      const response = await updateEpisodeNote({
        path: { episode_id: savedEpisodeId, note_id: noteId },
        body: { body },
      });
      if (response.error) {
        throw response.error;
      }
      if (renderedEpisodeIdRef.current !== savedEpisodeId) {
        return;
      }
      const updated = response.data;
      if (updated) {
        // Replaced in place: the entry keeps its position, because the server
        // orders the log by created_at and an edit does not move that.
        // Applied straight to state, the way a create is. No load can be
        // racing this one: a load hides the list behind the spinner, so there
        // is no note on screen to have started an edit from.
        setNotes(previous =>
          previous.map(entry => (entry.id === updated.id ? updated : entry))
        );
      }
      cancelEdit();
      showSuccess('Note updated', 'Your note was updated.');
    } catch (error) {
      // Left in edit mode with the text still in the box, so a failed save
      // does not cost the clinician what they just typed.
      console.error('Failed to update episode note:', error);
      showError('Failed to update note', 'Please try again.');
    } finally {
      setIsMutating(false);
    }
  };

  const pendingDeleteNote =
    notes.find(entry => entry.id === pendingDeleteNoteId) ?? null;

  const handleDelete = async (noteId: string) => {
    if (isBusy) {
      return;
    }
    const savedEpisodeId = episodeId;
    setIsMutating(true);
    try {
      const response = await deleteEpisodeNote({
        path: { episode_id: savedEpisodeId, note_id: noteId },
      });
      if (response.error) {
        throw response.error;
      }
      if (renderedEpisodeIdRef.current !== savedEpisodeId) {
        return;
      }
      setNotes(previous => previous.filter(entry => entry.id !== noteId));
      setPendingDeleteNoteId(null);
      showSuccess('Note deleted', 'Your note was removed from this episode.');
    } catch (error) {
      console.error('Failed to delete episode note:', error);
      showError('Failed to delete note', 'Please try again.');
      setPendingDeleteNoteId(null);
    } finally {
      setIsMutating(false);
    }
  };

  return (
    <section className={className} aria-label={title}>
      <h3 className='text-sm font-medium text-muted-foreground'>{title}</h3>
      {description && (
        <p className='mt-0.5 text-xs text-muted-foreground'>{description}</p>
      )}
      <div className='mt-2 space-y-2'>
        {isLoading ? (
          <div className='flex items-center gap-2 text-xs text-muted-foreground'>
            <Loader2 className='h-3 w-3 animate-spin' aria-hidden='true' />
            Loading notes...
          </div>
        ) : notes.length > 0 ? (
          // Capped and scrollable: this also renders inside a dialog that has
          // no height limit of its own, where a long log would push the footer
          // buttons off a narrow viewport.
          <ul
            ref={listRef}
            className='max-h-36 space-y-2 overflow-y-auto rounded-md border border-border bg-muted/30 p-2'
          >
            {notes.map(note => {
              const timestamp = formatEpisodeNoteTimestamp(note.created_at);
              const isOwnNote =
                !!currentUserId && note.created_by_id === currentUserId;
              const isEditing = editingNoteId === note.id;
              const authorLine = (
                <p className='mt-0.5 text-muted-foreground'>
                  {formatAuthorName(note)}
                  {timestamp ? ` · ${timestamp}` : null}
                  {wasEdited(note) ? ' · edited' : null}
                </p>
              );

              if (isEditing) {
                return (
                  <li key={note.id} className='text-xs'>
                    <Textarea
                      aria-label='Edit episode note'
                      value={editDraftBody}
                      onChange={event => setEditDraftBody(event.target.value)}
                      onKeyDown={event => {
                        if (
                          event.key === 'Enter' &&
                          (event.metaKey || event.ctrlKey)
                        ) {
                          event.preventDefault();
                          void handleSaveEdit(note.id);
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          cancelEdit();
                        }
                      }}
                      textareaSize='sm'
                      rows={3}
                      maxLength={MAX_NOTE_LENGTH}
                      disabled={isBusy}
                    />
                    <div className='mt-1 flex justify-end gap-2'>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={cancelEdit}
                        disabled={isBusy}
                      >
                        Cancel
                      </Button>
                      <Button
                        type='button'
                        variant='purple'
                        size='sm'
                        onClick={() => void handleSaveEdit(note.id)}
                        disabled={isBusy || !editDraftBody.trim()}
                      >
                        {isBusy ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </div>
                    {authorLine}
                  </li>
                );
              }

              return (
                <li key={note.id} className='text-xs'>
                  <div className='flex items-start justify-between gap-2'>
                    <p className='flex-1 whitespace-pre-wrap break-words'>
                      {note.body}
                    </p>
                    {canAdd && isOwnNote && (
                      <div className='flex shrink-0 items-center gap-1'>
                        <button
                          type='button'
                          aria-label='Edit note'
                          className='text-muted-foreground hover:text-foreground disabled:opacity-50'
                          onClick={() => startEdit(note)}
                          disabled={isBusy || editingNoteId !== null}
                        >
                          <Pencil className='h-3.5 w-3.5' aria-hidden='true' />
                        </button>
                        <button
                          type='button'
                          aria-label='Delete note'
                          className='text-muted-foreground hover:text-destructive disabled:opacity-50'
                          onClick={() => setPendingDeleteNoteId(note.id)}
                          disabled={isBusy || editingNoteId !== null}
                        >
                          <Trash2 className='h-3.5 w-3.5' aria-hidden='true' />
                        </button>
                      </div>
                    )}
                  </div>
                  {authorLine}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className='text-xs text-muted-foreground'>No episode notes yet.</p>
        )}

        {canAdd && (
          <>
            <Textarea
              aria-label='New episode note'
              value={draftBody}
              onChange={event => setDraftBody(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  void handleSave();
                }
              }}
              placeholder='Add a note shared by every task in this episode...'
              textareaSize='sm'
              rows={3}
              maxLength={MAX_NOTE_LENGTH}
              disabled={isBusy}
            />
            <div className='flex justify-end'>
              <Button
                type='button'
                variant='purple'
                size='sm'
                onClick={() => void handleSave()}
                disabled={isBusy || !draftBody.trim()}
              >
                {isBusy ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Adding...
                  </>
                ) : (
                  'Add note'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
      {/* A modal rather than an inline row, the way the rest of the product
          asks before a destructive action. Rendered only while one is pending,
          so it cannot be left armed against an entry that has since gone. */}
      {pendingDeleteNote && (
        <ConfirmationDialog
          open
          title='Delete note'
          message='This removes the note from this episode. It will no longer be visible on any task in the episode.'
          confirmLabel='Delete'
          loading={isMutating}
          onAccept={() => void handleDelete(pendingDeleteNote.id)}
          onCancel={() => setPendingDeleteNoteId(null)}
        />
      )}
    </section>
  );
};

export default EpisodeNotes;
